import {MuNamedWidgets, MuWidget} from "mu-widget/lib/MuWidget";
import {Triggers} from "mu-js-utils/lib/Triggers";
import {TNames} from "../../TNames";
import {openInPanel} from "../../appPanel";
import {BasePanelWidget} from "../../BasePanelWidget";
import {DayStats, foodStatComponents, MyJournal} from "../../types";
import {frontRouter} from "../../FrontRouter";
import {addFlash, elementToAiText, formatEnergy, formatWeight, isToday} from "../../utils";
import {BarMeterData} from "../BarMeter";
import {t} from "../../translate";

export class UiNutriInfoPanel extends MuWidget {
    beforeIndex() {
        this.container.classList.add("nutri-info-panel");
        this.muAppendContent(`
            <div mu="meter:BarMeter"></div>
        `);
    }

    protected dayStats: DayStats|null = null;

    afterIndex(): void | Promise<any> {
        Triggers.addHandler(TNames.dayLoaded, (ev, data: MyJournal) => {
            this.dayStats = data.dayStats;
            if (data.dayStats.recommendedValues === null) {
                this.muNamedWidget.meter.muBindData({
                    min: 0,
                    max: 1,
                    value: 1,
                    points: [],
                    color: '#ffffff00',
                    label: formatEnergy(data.dayStats.currentValues.energy, false, true)
                } as BarMeterData);
            } else {
                this.muNamedWidget.meter.muBindData({
                    min: 0,
                    max: data.dayStats.recommendedValues.energy,
                    value: data.dayStats.currentValues.energy,
                    points: [
                        { value: data.dayStats.recommendedValues.energy, color: '#FF0000' },
                        ...(isToday(data.day)
                            ? [ { value: data.dayStats.dayTimeRecommended, color: '#009900' } ]
                            : [ ]),
                    ],
                    color: [
                        { color: "#3333ff", value: 0 },
                        { color: "#009900", value: data.dayStats.dayTimeRecommended },
                        { color: "#FF5500", value: data.dayStats.recommendedValues.energy * 1.05 },
                        { color: "#FF0000", value: data.dayStats.recommendedValues.energy * 1.5 },
                    ],
                    label: formatEnergy(data.dayStats.currentValues.energy, false, true)
                        + ' / ' + formatEnergy(data.dayStats.dayTimeRecommended, false, true)
                        + ' / ' + formatEnergy(data.dayStats.recommendedValues.energy, true, true),

                } as BarMeterData);
            }
        });
    }

    async container_click() {
        (await openInPanel('UiNutriInfoDetails')).muBindData(this.dayStats);
    }
}

export class UiNutriInfoData extends MuWidget {
    beforeIndex() {
        this.muAppendContent(
            `
            <dl class="num-pairs" mu="#showDetailData:@visible">
                <dt>Aktuální hodnota: </dt><dd mu="#currentEnergy|formatEnergy"></dd>
                <dt mu="#isToday:@visible">Cíl pro teď: <span mu="#dayTimePer"></span></dt><dd mu="#isToday:@visible;dayTimeRecommended|formatEnergy"></dd>
                <dt>Cíl pro celý den: <span mu="#totalPer"></span></dt><dd mu="#recommendedEnergy|formatEnergy"></dd>
                <dt>
                    Energie spotřebovaná aktivitami:
                    <!-- <div class="num-pairs__details">
                        <span mu="#activityLevel|prepend('icon-size--24 icon--')"
                        Aktivní den
                    </div> -->
                </dt><dd mu="#energyFromActivities|formatEnergy"></dd>
                <dt>Bazální energie: </dt><dd mu="#basalEnergy|formatEnergy"></dd>
                <dt>Voda: </dt><dd><span mu="#water"></span><spam mu="bCopy">ml</spam></dd>
            </dl>

            <h3>Podle složek potravy</h3>
            <table class="nutri-info--details__table">
                <thead>
                    <tr>
                        <th></th>
                        <th class="text-left" mu="#showDetailData:@visible">Cíl</th>
                        <th class="text-left">Aktuálně</th>
                        <th class="text-left" mu="#showDetailData:@visible">Podíl</th>
                    </tr>
                </thead>
                <tbody mu="#lines:@foreach">
                    <tr mu=":.@line">
                        <th class="nutri-info--details__cell-label" mu="#label"></th>
                        <td class="nutri-info--details__cell-recommended" mu="#recommended;showDetailData:@visible"></td>
                        <td class="nutri-info--details__cell-current" mu="#current"></td>
                        <td class="nutri-info--details__cell-completed" mu="#completed;showDetailData:@visible"></td>
                    </tr>
                </tbody>
            </table>
            <p mu="#showDetailData|not:@visible">
                Pro zobrazení doporučených hodnot nastav/doplň své údaje.
            </p>
            <div>
                <a mu="bClientData">Zadat moje údaje</a>
            </div>`
        );
    }

    afterIndex(): void | Promise<any> {
        frontRouter.prepareAnchor(this.ui.bClientData, 'clientData', {});
    }

    async bCopy_click() {
        try {
            await navigator.clipboard.writeText(elementToAiText(this.container));
            addFlash(t('nutriInfoCopied'), 'ok');
        } catch {
            addFlash(t('nutriInfoCopyFailed'), 'error');
        }
    }

    muBindData(srcData: DayStats&any) {
        const showDetailData = !!srcData.recommendedValues;
        srcData.lines = [];
        for (const field of foodStatComponents) {
            const row: any = {
                label: field.label,
                showDetailData,
            };
            row.current = srcData.currentValues[field.key];
            row.recommended = srcData.recommendedValues?.[field.key];

            row.completed = row.recommended
                ? Math.round((row.current * 100) / row.recommended).toString() + "%"
                : '-';

            if (field.unit === '#energy') {
                row.current = formatEnergy(row.current);
                row.recommended = formatEnergy(row.recommended);
            } else {
                row.current = formatWeight(row.current);
                row.recommended = formatWeight(row.recommended);
            }

            srcData.lines.push(row);
        }
        super.muBindData({
            ...srcData,
            showDetailData,
            currentEnergy: srcData.currentValues.energy,
            recommendedEnergy: srcData.recommendedValues?.energy,
            dayTimePer: srcData.dayTimeRecommended
                ? '(' + Math.round((srcData.currentValues.energy / srcData.dayTimeRecommended) * 100) + '%)'
                : '',
            totalPer: srcData.recommendedValues?.energy
                ? '(' + Math.round((srcData.currentValues.energy / srcData.recommendedValues?.energy) * 100) + '%)'
                : '',
        });
    }
}

export class UiNutriInfoDetails extends BasePanelWidget {
    // @ts-ignore
    muNamedWidget: MuNamedWidgets<{ content: UiNutriInfoData }>;

    beforeIndex() {
        this.muAppendContent(`<div mu="content:UiNutriInfoData"></div>`);
    }

    afterIndex(): void | Promise<any> {
        Triggers.addHandler(TNames.dayLoaded, (_ev, data) => {
            this.muBindData({
                ...data.dayStats,
                isToday: isToday(data.day),
            });
        });
        this.setTitle('Výživové hodnoty za den');
    }

    muBindData(srcData: DayStats&any) {
        this.muNamedWidget.content.muBindData(srcData);
    }
}